import { TeamRoleService } from '../../../services/team-role/team-role.service';
import { UserService } from '../../../services/user/user.service';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { IptPhase } from '../../../model/team/IptPhase';
import { GetTeamRoles } from '../../../services/team-role/GetTeamRoles';
import { TeamMemberEntry, TeamRoleUtil } from '../../../TeamRoleUtil';
import { GetUsers } from '../../../services/user/GetUsers';
import { StringUtil } from '../../../StringUtil';
import {
  ReadOnlyState
} from '../../../../project-details/ReadOnlyStateManager';

@Component({
  selector: 'digires-team-member-entry',
  templateUrl: './team-member-entry.component.html',
  styleUrls: ['./team-member-entry.component.scss']
})
export class TeamMemberEntryComponent implements OnInit, OnDestroy, OnChanges {
  readonly destroy$: Subject<boolean> = new Subject<boolean>();

  @Output() onDelete = new EventEmitter<void>();
  @Input() entry: TeamMemberEntry;
  @Input() iptPhase: IptPhase;
  @Input() readOnly: ReadOnlyState;
  @Input() showDepartment = false;
  @Input() showCheckbox: boolean = true;

  @Input() allUsers: GetUsers[];
  @Input() allRoles: GetTeamRoles[];
  filteredUsers: GetUsers[];
  filteredRoles: GetTeamRoles[];
  util: TeamRoleUtil;

  constructor(
    private userService: UserService,
    private roleService: TeamRoleService,
  ) { }

  ngOnInit() {
    if (!this.allUsers) {
      this.userService
        .observeUserList()
        .pipe(takeUntil(this.destroy$))
        .subscribe(userList => {
          this.allUsers = userList.sort((a, b) => a.fullName.localeCompare(b.fullName));
          this.filteredUsers = userList;
        });
      this.userService.fetchAllUsers();
    }
    else {
      this.filteredUsers = [...this.allUsers];
    }
    if (!this.allRoles) {
      this.roleService
        .fetchAllTeamRoles()
        .then(list => {
          this.allRoles = list;
          this.filteredRoles = list;
        });
    }
    else {
      this.filteredRoles = [...this.allRoles];
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngOnChanges(): void {
    this.util = new TeamRoleUtil(this.iptPhase);
  }

  isDeputy(): boolean {
    const role = this.entry.getRole();
    return role && this.util.isDeputyManager(role);
  }

  getAccountNamePlaceholder(): string {
    if (this.entry.isRoleChangePossible()) {
      return 'Team Member';
    }
    return this.entry.getRoleName();
  }

  getUserName(user: GetUsers): string {
    if (user && user.fullName) {
      return user.fullName;
    }
    return null;
  }

  onFilterUsers(event: any): void {
    const value = event.target.value;
    if (!value) {
      this.onUserSelected(null);
      return;
    }
    const lowerCaseVal: string = value.toLowerCase();
    this.filteredUsers = this.allUsers.filter(user => {
      return StringUtil.filteredIncludes(user.fullName, lowerCaseVal);
    });

    const foundOpt = this.filteredUsers.find(user => {
      return user.fullName.toLowerCase() === lowerCaseVal;
    });
    if (foundOpt) {
      this.onUserSelected(foundOpt);
    }
  }

  onUserSelected(value: GetUsers): void {
    this.filteredUsers = this.allUsers;
    this.entry.fg.patchValue({user: value});
  }

  getUserErrorMessage(): string {
    if (this.entry.fg && this.entry.fg.invalid) {
      const user = this.entry.fg.value.user;
      if (!user || !user.accountName) {
        return 'Entered User is not a DigiRES User.';
      }
    }
    return null;
  }

  getRoleNameForRole(role: any): string {
    if (role?.name) {
      return role.name;
    }
    return role;
  }

  onFilterRoles(event: any): void {
    const value = event.target.value;
    if (!value) {
      this.onRoleSelected(null);
      return;
    }
    const lowerCaseVal: string = value.toLowerCase();
    this.filteredRoles = this.allRoles.filter(role => {
      return role.name.toLowerCase().includes(lowerCaseVal);
    });

    const foundOpt = this.filteredRoles.find(role => {
      return role.name.toLowerCase() === lowerCaseVal;
    });
    if (foundOpt) {
      this.onRoleSelected(foundOpt);
    }
  }

  onRoleSelected(value: GetTeamRoles): void {
    this.filteredRoles = this.allRoles;
    this.entry.fg.patchValue({role: value});
  }

  isRoleMandatory(role: GetTeamRoles): boolean {
    return this.util.isMandatory(role);
  }

  isConstructionRoleMandatory(role: GetTeamRoles): boolean {
    return role.settings[IptPhase.CONSTRUCTION].core;
  }

  isDuplicateIconVisible(): boolean {
    return this.entry.firstDuplicated || this.entry.duplicated;
  }

  isRoleDeletable(): boolean {
    return this.entry.duplicated || this.entry.firstDuplicated
      || this.util.isRoleDeletable(this.entry.getRole());
  }

}
